// capabilities.ts — Wave 1 (roles[]-retirement). The SINGLE frontend source the UI gates on,
// reading the 5 orthogonal access axes off the /me store user:
//   RANK    (user.rank)                      — 5-rung ladder Viewer<Member<Lead<Manager<Admin
//   LICENSE (user.license)                   — clinical identity (MD/DO/NP/PA/RN/…)
//   GRANTS  (responsibilities[], module_admin_for[]) — capability grants (BE user_grants, flattened on /me)
//   SCOPE   (scope_location_ids[], scope_module_keys[]) — reach (BE user_scope, flattened on /me)
//   ORG     (scope_organization_id, is_org_level)
// + the DISTINCT cross-tenant god signal user.is_platform_admin (PHI guardrail #1: NEVER rank, NEVER user_type).
//
// ADDITIVE in Wave 1: built but NOT wired into existing gates. Each gate flips to this only WITH its
// backend cutover in a later wave; until then lib/gating.ts + the store helpers remain the live gates.
// These are server-MIRRORS (display/UX only); the server stays authoritative.

import { useMemo } from 'react';
import { useQuery, type QueryClient } from '@tanstack/react-query';
import type { User, LicenseType, ClinicalLicense } from '../types';
import { useAuthStore } from '../store';
import { canAdministerCurrentPractice as canAdministerCurrentPracticeGate } from './gating';
import { accessApi } from './api';
import { errStatus } from './apiError';

// ── RANK ────────────────────────────────────────────────────────────────────────
export const RANK_LADDER = ['Viewer', 'Member', 'Lead', 'Manager', 'Admin'] as const;
export type Rank = (typeof RANK_LADDER)[number];

/** True when the user's rank rung is >= `required`. Reads user.rank ONLY (no role-name derive).
 *  A missing/unknown rank fails CLOSED for any rung above Viewer. */
export function rankAtLeast(user: User | null | undefined, required: Rank): boolean {
  if (!user) return false;
  const need = RANK_LADDER.indexOf(required);
  if (need < 0) return false;
  return RANK_LADDER.indexOf((user.rank ?? '') as Rank) >= need;
}

// ── PLATFORM-ADMIN (the cross-tenant god signal) ─────────────────────────────────
/** The cross-tenant PHI god-bypass — a DISTINCT identity signal (users.is_platform_admin), never
 *  rank, never user_type. Wave-1 transitional fallback: until the BE returns the column, fall back
 *  to the live authority roles[].includes('platform_admin') so behaviour is unchanged at cutover. */
export function isPlatformAdmin(user: User | null | undefined): boolean {
  if (!user) return false;
  if (typeof user.is_platform_admin === 'boolean') return user.is_platform_admin;
  const roles = user.roles ?? (user.role ? [user.role] : []);
  return roles.includes('platform_admin');
}

// ── GRANTS (capabilities) ────────────────────────────────────────────────────────
export type GrantType = 'responsibility' | 'module_admin';

/** True when the user holds GRANT `key` of `type`. Mirrors BE user_grants; /me flattens these onto
 *  user.responsibilities[] (type 'responsibility') and user.module_admin_for[] (type 'module_admin'). */
export function hasGrant(user: User | null | undefined, type: GrantType, key: string): boolean {
  if (!user) return false;
  const arr = type === 'responsibility' ? user.responsibilities : user.module_admin_for;
  return Array.isArray(arr) && (arr as string[]).includes(key);
}

// ── GENERIC CAPABILITY GRANTS (grant_type='capability') ──────────────────────────
/** True when the user holds the generic capability grant `key` (BE user_grants grant_type='capability',
 *  flattened onto user.capabilities[] by /me — e.g. 'records_release.approve'). Distinct from the
 *  responsibility / module_admin grants above. A missing/undefined array fails CLOSED. */
export function hasCapability(user: User | null | undefined, key: string): boolean {
  if (!user) return false;
  return Array.isArray(user.capabilities) && user.capabilities.includes(key);
}

// ── PRACTICE-KEYED CAPABILITIES (access core, DESIGN 3.7) ─────────────────────────
/** The three keys whose "may use it" is a fact about (user, PRACTICE, key), not (user, key).
 *  Mirrors BE services/auth/grantKeys.ts PRACTICE_KEYED_CAPABILITY_KEYS. */
export type PracticeKeyedKey = 'central_fax.send' | 'ai_assist.use' | 'ai_assist.open_ask';

/** GET /api/access/me: the caller's own answers at the SELECTED practice (the server's
 *  holdsPracticeCapability semantics per key; god arm first). The module leg is NOT in it. */
export interface PracticeAccessMe {
  practice_id: string | null;
  is_platform_admin: boolean;
  keys: Partial<Record<PracticeKeyedKey, boolean>>;
}

/** What the FE knows about /access/me right now. `notFound` = the server answered 404 (the route is
 *  not there), the one state that falls back to the old practice-blind read. Anything else without
 *  an answer (loading, any other error, an answer for another practice) is `me: undefined`. */
export interface PracticeAccessState {
  me: PracticeAccessMe | undefined;
  notFound: boolean;
}

/** May this user use practice-keyed `key` at the selected practice? Display/UX only; the server's
 *  gate stays the authority, and the module leg stays with each door.
 *  - /access/me answered: `me.is_platform_admin || me.keys[key] === true`.
 *  - /access/me is 404: the old read, `hasCapability(user, key) || isPlatformAdmin(user)`.
 *  - otherwise (loading, error): false. FAILS CLOSED, like the doors' own module leg. */
export function canUsePracticeKey(
  user: User | null | undefined,
  state: PracticeAccessState,
  key: PracticeKeyedKey,
): boolean {
  if (!user) return false;
  if (state.notFound) return hasCapability(user, key) || isPlatformAdmin(user);
  const me = state.me;
  if (!me) return false;
  return me.is_platform_admin === true || me.keys?.[key] === true;
}

/** The react-query keys of the /api/access reads. Every access surface uses these, so one
 *  invalidation (below) refreshes all of them after a change set. */
export const accessQueryKeys = {
  practice: (practiceId: string) => ['access', practiceId] as const,
  history: (practiceId: string) => ['access-history', practiceId] as const,
  person: (userId: string) => ['access-person', userId] as const,
  overview: () => ['access-overview'] as const,
  me: (practiceId: string | null) => ['access-me', practiceId] as const,
};

/** After ANY applied change set at `practiceId`: every access view, the caller's own answers at
 *  every practice, the old fax sender keys (FaxConfigPage) and each touched person's user detail. */
export function invalidateAfterAccessChange(
  queryClient: QueryClient,
  practiceId: string,
  userIds: string[] = [],
): void {
  queryClient.invalidateQueries({ queryKey: accessQueryKeys.practice(practiceId) });
  queryClient.invalidateQueries({ queryKey: accessQueryKeys.history(practiceId) });
  queryClient.invalidateQueries({ queryKey: ['access-person'] });
  queryClient.invalidateQueries({ queryKey: accessQueryKeys.overview() });
  queryClient.invalidateQueries({ queryKey: ['access-me'] });
  queryClient.invalidateQueries({ queryKey: ['central-fax-config', practiceId] });
  queryClient.invalidateQueries({ queryKey: ['central-fax-senders', practiceId] });
  queryClient.invalidateQueries({ queryKey: ['central-fax-sender-candidates', practiceId] });
  for (const userId of userIds) queryClient.invalidateQueries({ queryKey: ['user-detail', userId] });
}

/**
 * The caller's own practice-keyed answers at the SELECTED practice (GET /api/access/me), keyed by
 * that practice so a switch never reads another practice's answer. user.capabilities[] is
 * practice-BLIND for a platform user; this read is not, and it carries the platform-admin god arm.
 * `canUsePracticeKey(key)` is the one read every door uses (see canUsePracticeKey above). The
 * module leg is NOT in this answer: each door keeps its own.
 */
export function useMyPracticeAccess() {
  const { user, currentPractice } = useAuthStore();
  const practiceId = currentPractice?.practice_id ?? null;
  const query = useQuery({
    queryKey: accessQueryKeys.me(practiceId),
    enabled: !!user?.user_id,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    retry: false,
    queryFn: async () => {
      const res = await accessApi.me();
      return res.data.data as PracticeAccessMe;
    },
  });
  const answer = query.data;
  // The header rides on the store at request time, so an answer can in principle belong to a
  // practice the user has just left. An answer for any practice but this one is no answer.
  const me = answer && (answer.practice_id ?? null) === practiceId ? answer : undefined;
  const state: PracticeAccessState = { me, notFound: errStatus(query.error) === 404 };
  return {
    ...state,
    canUsePracticeKey: (key: PracticeKeyedKey) => canUsePracticeKey(user, state, key),
  };
}

// ── SCOPE (reach) ─────────────────────────────────────────────────────────────────
export type ScopeType = 'location' | 'module' | 'organization';

/** True when the user is scoped to `key` on `type`. EMPTY array = practice-wide (the BE scopeChecker
 *  convention): an empty location/module scope returns TRUE (no restriction). organization compares
 *  scope_organization_id. */
export function hasScope(user: User | null | undefined, type: ScopeType, key: string): boolean {
  if (!user) return false;
  if (type === 'organization') return (user.scope_organization_id ?? null) === key;
  const arr = type === 'location' ? user.scope_location_ids : user.scope_module_keys;
  if (!Array.isArray(arr) || arr.length === 0) return true; // empty = practice-wide
  return arr.includes(key);
}

// ── LICENSE (clinical identity) ─────────────────────────────────────────────────
/** Mirrors the backend permissionEngine.isLicenseExpired (and denialService.reviewerLicenseExpired):
 *  a MISSING or MALFORMED expires_at is open-ended (NOT expired); a parseable expiry in the past IS
 *  expired. Display/UX only — the server stays authoritative. */
function licenseExpired(license: ClinicalLicense): boolean {
  if (!license.expires_at) return false;          // open-ended license — no expiry recorded
  const expiry = Date.parse(license.expires_at);
  if (Number.isNaN(expiry)) return false;         // malformed string — treat as not expired
  return Date.now() > expiry;
}

/** True when the user holds a CURRENT clinical license whose type is one of `types`. user.license is a
 *  single ClinicalLicense (or null). An EXPIRED license (expires_at present AND in the past) is treated
 *  as NOT licensed, mirroring the backend license gate; NULL/malformed expires_at = open-ended (NOT
 *  expired). FE display correctness only — the server stays authoritative. */
export function hasLicense(user: User | null | undefined, types: LicenseType[]): boolean {
  if (!user || !user.license) return false;
  if (!types.includes(user.license.type)) return false;
  return !licenseExpired(user.license);
}

// ── BUNDLE + HOOK ─────────────────────────────────────────────────────────────────
export interface Capabilities {
  user: User | null;
  isPlatformAdmin: boolean;
  rankAtLeast: (rank: Rank) => boolean;
  hasGrant: (type: GrantType, key: string) => boolean;
  hasCapability: (key: string) => boolean;
  hasScope: (type: ScopeType, key: string) => boolean;
  hasLicense: (types: LicenseType[]) => boolean;
  /** Reuses lib/gating.canAdministerCurrentPractice() (the live Wave-3d logic) unchanged. */
  canAdministerCurrentPractice: boolean;
}

/** Reactive accessor for the capability surface. Subscribes to user + the two practice slices so
 *  canAdministerCurrentPractice recomputes on a practice switch (the underlying gate reads a
 *  getState() snapshot, so we depend on those slices to drive recompute). */
export function useCapabilities(): Capabilities {
  const user = useAuthStore((s) => s.user);
  const currentPractice = useAuthStore((s) => s.currentPractice);
  const assignedPractices = useAuthStore((s) => s.assignedPractices);
  return useMemo<Capabilities>(() => ({
    user,
    isPlatformAdmin: isPlatformAdmin(user),
    rankAtLeast: (rank) => rankAtLeast(user, rank),
    hasGrant: (type, key) => hasGrant(user, type, key),
    hasCapability: (key) => hasCapability(user, key),
    hasScope: (type, key) => hasScope(user, type, key),
    hasLicense: (types) => hasLicense(user, types),
    canAdministerCurrentPractice: canAdministerCurrentPracticeGate(),
  // eslint-disable-next-line react-hooks/exhaustive-deps -- gate reads getState(); these slices drive recompute
  }), [user, currentPractice, assignedPractices]);
}
